import { useEffect } from "react";
import { Sparkles, MessageSquarePlus, UserPlus } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import PageHero from "@/components/PageHero";
import { useLanguage } from "@/i18n/LanguageContext";
import { trackEvent } from "@/analytics/ga";

/**
 * V2 preview landing page (Phase 24).
 *
 * Route: `/v2` (English) + `/ar/v2` (Arabic RTL).
 *
 * Purpose per the spec:
 *   - Explain what SAS V2 is building toward WITHOUT overpromising.
 *   - Signal the direction (expanded directory, no-VPN filter, new
 *     content, upcoming collaborations, community input).
 *   - Anchor two clearly different CTAs — "Submit a suggestion"
 *     (primary, to the V2 feedback form) and "Join as a contributor"
 *     (secondary, reuses the existing volunteer/contributor form with
 *     `?source=v2_article`).
 *
 * Phase 24 UX & copy refinement (post-review pass):
 *   - The five "pillars" are editorial content, so they are rendered as a
 *     vertical 01–05 editorial list (small accent sequence number, large
 *     foreground heading, comfortable-line-length paragraph, subtle
 *     divider) instead of an icon card grid. Reading > scanning.
 *   - Defensive / internal-planning wording removed ("nothing is final",
 *     "no promises", "only after confirmation").
 *   - CTA area simplified: helper lines under the buttons removed; the
 *     two actions are now semantically distinct (idea vs. joining).
 *
 * Guardrails from the spec (do NOT weaken):
 *   - Never say "150 tools are already live." The directory is being
 *     DESIGNED to support up to 150 tools.
 *   - Never name unconfirmed partners.
 *   - Never use "Beta 2".
 *   - Tone: credible, calm, forward-looking, community-driven.
 */

const COPY = {
  ar: {
    eyebrow: "SAS V2",
    heroTitle: "SAS V2: نبني الإصدار القادم مع مجتمعنا",
    heroLead:
      "الإصدار القادم من منصة مجتمع الذكاء الاصطناعي السوري يتطوّر من دليل أدوات مفيد إلى منصّة أكثر عملية وقابلية للاكتشاف، مصمّمة بناءً على ما يحتاجه المجتمع فعليًّا.",
    whyTitle: "لماذا SAS V2؟",
    whyBody:
      "بعد ملاحظات المستخدمين وطلبات التعاون خلال الأشهر الماضية، قرّرنا الانتقال من نسخة تعرض الأدوات إلى نسخة توجّه المستخدم إلى الأداة المناسبة بأسرع طريقة، مع اهتمام أكبر بالمحتوى العلمي التطبيقي والوصول من داخل سوريا.",
    whatIsComingTitle: "ما القادم في SAS V2؟",
    whatIsComingLead:
      "خمسة محاور نعمل عليها للإصدار القادم، وسيساهم رأي المجتمع في تحديد تفاصيلها وأولوياتها.",
    features: [
      {
        id: "directory",
        num: "01",
        title: "دليل أدوات أوسع",
        body:
          "نعمل على بنية جديدة لدليل الأدوات، مصممة لاستيعاب ما يصل إلى 150 أداة مجانية أو ذات خطة مجانية، مع معايير أوضح للاختيار وسهولة الوصول.",
      },
      {
        id: "no-vpn",
        num: "02",
        title: "يعمل بدون VPN",
        body:
          "فلتر جديد يساعدك على معرفة الأدوات التي يمكن استخدامها مباشرة من سوريا، وتقليل الوقت الضائع في تجربة خدمات غير متاحة.",
      },
      {
        id: "content",
        num: "03",
        title: "محتوى علمي وعملي جديد",
        body:
          "محتوى جديد يركّز على الاستخدام المباشر للذكاء الاصطناعي في الدراسة والبحث العلمي والعمل اليومي، بلغة واضحة وقابلة للتطبيق.",
      },
      {
        id: "collaborations",
        num: "04",
        title: "تعاونات جديدة",
        body:
          "نعمل على مجموعة من التعاونات الجديدة التي ستوسّع ما يمكننا تقديمه للمجتمع، وسيتم الإعلان عن تفاصيلها قريبًا.",
      },
      {
        id: "community",
        num: "05",
        title: "نبنيه مع المجتمع",
        body:
          "قبل تثبيت أولويات V2 نريد سماع رأيك: أدوات تريد رؤيتها، محتوى تحتاجه، ميزات تقترحها، أو مشكلات وصول تواجهها.",
      },
    ],
    ctaSectionEyebrow: "شارك في صياغة SAS V2",
    ctaSectionTitle: "اقتراحك قد يصبح جزءًا من SAS V2",
    ctaSectionLead: "نريد أن نعرف ما الذي تحتاجه فعلًا في الإصدار القادم.",
    ctaPrimary: "قدّم اقتراحًا",
    ctaSecondary: "انضم كمساهم",
    closing:
      "SAS V2 يتشكل الآن — وسنشارككم المزيد مع تقدّم العمل.",
    // Social / SEO metadata (Phase 24d). Title + description are the
    // approved sharing copy; the rest drives OG/Twitter/hreflang tags.
    metaTitle: "SAS V2 | مجتمع الذكاء الاصطناعي السوري",
    metaDesc:
      "تعرّف على ما نعمل عليه في SAS V2، وشاركنا اقتراحك للمساهمة في تشكيل الإصدار القادم.",
    siteName: "مجتمع الذكاء الاصطناعي السوري",
    ogImageAlt: "SAS V2 — مجتمع الذكاء الاصطناعي السوري",
    ogLocale: "ar_AR",
    ogLocaleAlt: "en_US",
  },
  en: {
    eyebrow: "SAS V2",
    heroTitle: "SAS V2: Building the next version with our community",
    heroLead:
      "The next version of the Syrian AI Society platform is evolving from a useful directory into a more practical, discoverable platform, shaped by what our community actually needs.",
    whyTitle: "Why SAS V2",
    whyBody:
      "Based on user feedback and collaboration requests over the last months, we're moving from a version that lists tools to a version that guides you to the right tool as quickly as possible, with a stronger focus on applied scientific content and access from inside Syria.",
    whatIsComingTitle: "What's coming in SAS V2?",
    whatIsComingLead:
      "Five areas we are developing for the next release, with community feedback helping shape their details and priorities.",
    features: [
      {
        id: "directory",
        num: "01",
        title: "Expanded tools directory",
        body:
          "We are developing a broader tools-directory structure designed to support up to 150 free or free-tier tools, with clearer selection criteria and easier discovery.",
      },
      {
        id: "no-vpn",
        num: "02",
        title: "Works without VPN",
        body:
          "A new filter will help identify tools that can be used directly from Syria, reducing time lost testing inaccessible services.",
      },
      {
        id: "content",
        num: "03",
        title: "New scientific & practical content",
        body:
          "New content focused on practical AI use in study, scientific research, and everyday workflows, written to be clear and actionable.",
      },
      {
        id: "collaborations",
        num: "04",
        title: "New collaborations",
        body:
          "We are working on new collaborations that will expand what we can offer the community. More details will be announced soon.",
      },
      {
        id: "community",
        num: "05",
        title: "Built with the community",
        body:
          "Before V2 priorities are finalized, we want to hear from you: tools you want to see, content you need, features you suggest, or access problems you face.",
      },
    ],
    ctaSectionEyebrow: "Help shape SAS V2",
    ctaSectionTitle: "Your suggestion could become part of SAS V2",
    ctaSectionLead: "Tell us what you actually need from the next release.",
    ctaPrimary: "Submit a suggestion",
    ctaSecondary: "Join as a contributor",
    closing:
      "SAS V2 is taking shape — and we'll share more as the work progresses.",
    metaTitle: "SAS V2 | Syrian AI Society",
    metaDesc:
      "Discover what we're building for SAS V2 and share your ideas to help shape the next release.",
    siteName: "Syrian AI Society",
    ogImageAlt: "SAS V2 — Syrian AI Society",
    ogLocale: "en_US",
    ogLocaleAlt: "ar_AR",
  },
};

/* ---------------------------------------------------------------------- */
/* Social sharing / SEO metadata (Phase 24d)                               */
/*                                                                        */
/* Same runtime `<head>` injection pattern used by ToolDetail and         */
/* ArticleDetail — no new dependency. Two differences vs. the older       */
/* helpers: (1) every setter returns an undo function that RESTORES the   */
/* previous value (so the site-wide defaults in `public/index.html` come  */
/* back intact after client-side navigation), and (2) we also manage      */
/* `<link rel="canonical">` + `hreflang` alternates.                       */
/*                                                                        */
/* IMPORTANT — reach of these tags: WhatsApp / Telegram / Facebook / X    */
/* do not execute JS, so for THEIR previews the static defaults in        */
/* `public/index.html` apply. The tags below are honoured by JS-rendering  */
/* crawlers (Google) and are the single source of truth if a pre-render   */
/* step is added later.                                                   */
/* ---------------------------------------------------------------------- */

/** Dedicated V2 share image. Replace the FILE at this path to swap the
 *  artwork — no code change needed. 1200×630 JPG, keep under ~300 KB. */
const V2_OG_IMAGE_PATH = "/og/sas-v2-og.jpg";

/** Language-aware routes for canonical / hreflang. */
const V2_PATHS = { en: "/v2", ar: "/ar/v2" };

/** Public origin for absolute URLs. Mirrors the backend's `PUBLIC_SITE_URL`
 *  convention; falls back to the current origin so preview environments
 *  produce valid, resolvable URLs without any configuration. */
function siteOrigin() {
  const configured = (process.env.REACT_APP_PUBLIC_SITE_URL || "").trim();
  if (configured) return configured.replace(/\/+$/, "");
  return window.location.origin;
}

function setMeta(attr, name, content) {
  if (!content) return () => {};
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  const created = !el;
  const prev = el ? el.getAttribute("content") : null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (created) el.remove();
    else if (prev !== null) el.setAttribute("content", prev);
  };
}

function setLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  const created = !el;
  const prev = el ? el.getAttribute("href") : null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return () => {
    if (created) el.remove();
    else if (prev !== null) el.setAttribute("href", prev);
  };
}

export default function V2Preview() {
  const { lang } = useLanguage();
  const c = COPY[lang] || COPY.en;

  /* Analytics + SEO/social metadata on mount. */
  useEffect(() => {
    trackEvent("v2_article_view", { language: lang });

    const origin = siteOrigin();
    const pageUrl = origin + (V2_PATHS[lang] || V2_PATHS.en);
    const imageUrl = origin + V2_OG_IMAGE_PATH;

    const prevTitle = document.title;
    document.title = c.metaTitle;

    const undo = [
      setMeta("name", "description", c.metaDesc),
      // Open Graph (WhatsApp, Telegram, Facebook, LinkedIn, Signal…)
      setMeta("property", "og:type", "website"),
      setMeta("property", "og:site_name", c.siteName),
      setMeta("property", "og:title", c.metaTitle),
      setMeta("property", "og:description", c.metaDesc),
      setMeta("property", "og:url", pageUrl),
      setMeta("property", "og:image", imageUrl),
      setMeta("property", "og:image:width", "1200"),
      setMeta("property", "og:image:height", "630"),
      setMeta("property", "og:image:alt", c.ogImageAlt),
      setMeta("property", "og:locale", c.ogLocale),
      setMeta("property", "og:locale:alternate", c.ogLocaleAlt),
      // Twitter / X
      setMeta("name", "twitter:card", "summary_large_image"),
      setMeta("name", "twitter:title", c.metaTitle),
      setMeta("name", "twitter:description", c.metaDesc),
      setMeta("name", "twitter:image", imageUrl),
      setMeta("name", "twitter:image:alt", c.ogImageAlt),
      // Canonical + language alternates
      setLink("canonical", pageUrl),
      setLink("alternate", origin + V2_PATHS.en, "en"),
      setLink("alternate", origin + V2_PATHS.ar, "ar"),
      setLink("alternate", origin + V2_PATHS.en, "x-default"),
    ];

    return () => {
      document.title = prevTitle;
      // Undo in reverse order so restored values are the originals.
      for (let i = undo.length - 1; i >= 0; i -= 1) undo[i]();
    };
  }, [lang, c]);

  return (
    <article data-testid="v2-preview-page">
      <PageHero
        eyebrow={c.eyebrow}
        title={c.heroTitle}
        lead={c.heroLead}
      />

      {/* Why V2 */}
      <section
        className="sas-section-sm border-t border-[color:var(--sas-line)]"
        data-testid="v2-why"
      >
        <div className="sas-container">
          <div className="max-w-3xl">
            <div className="sas-eyebrow mb-2">
              <Sparkles size={12} className="inline me-1 -mt-0.5" aria-hidden />
              {c.whyTitle}
            </div>
            <h2 className="sas-h2">{c.whyTitle}</h2>
            <p className="sas-body mt-4 text-[15.5px] leading-relaxed">
              {c.whyBody}
            </p>
          </div>
        </div>
      </section>

      {/* What's coming — editorial 01–05 list (no icons, no card grid) */}
      <section className="sas-section-sm" data-testid="v2-what-is-coming">
        <div className="sas-container">
          <div className="max-w-3xl">
            <h2 className="sas-h2">{c.whatIsComingTitle}</h2>
            <p className="sas-body mt-3 text-[15px] max-w-[62ch]">
              {c.whatIsComingLead}
            </p>
          </div>

          <ol
            className="mt-10 sm:mt-12 max-w-4xl list-none m-0 p-0 border-b border-[color:var(--sas-line)]"
            data-testid="v2-feature-list"
          >
            {c.features.map((f) => (
              <li
                key={f.id}
                className="grid grid-cols-[2.75rem_minmax(0,1fr)] sm:grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 sm:gap-x-8 py-7 sm:py-9 border-t border-[color:var(--sas-line)]"
                data-testid={`v2-feature-${f.id}`}
              >
                {/* Sequence number — small, accent-coloured, aligned to the
                    heading's cap line. `aria-hidden` because the <ol> already
                    conveys order to assistive tech. */}
                <span
                  className="pt-[0.45rem] sm:pt-[0.6rem] text-[12.5px] sm:text-[13.5px] font-semibold tracking-[0.14em] tabular-nums text-[color:var(--sas-accent)] select-none"
                  aria-hidden
                  data-testid={`v2-feature-${f.id}-num`}
                >
                  {f.num}
                </span>
                <div className="min-w-0">
                  <h3
                    className="text-[1.25rem] sm:text-[1.5rem] lg:text-[1.625rem] font-semibold leading-[1.3] text-[color:var(--sas-ink)]"
                    data-testid={`v2-feature-${f.id}-title`}
                  >
                    {f.title}
                  </h3>
                  <p
                    className="mt-3 text-[15.5px] sm:text-[16.5px] leading-[1.85] text-[color:var(--sas-ink-2)] max-w-[62ch]"
                    data-testid={`v2-feature-${f.id}-body`}
                  >
                    {f.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Community CTA — Submit a suggestion (primary) / Join as a contributor (secondary) */}
      <section className="sas-section-sm" data-testid="v2-cta-section">
        <div className="sas-container">
          <div className="rounded-2xl border border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)]/25 px-6 py-8 sm:px-10 sm:py-12">
            <div className="max-w-2xl">
              <div className="sas-eyebrow mb-2">{c.ctaSectionEyebrow}</div>
              <h2 className="sas-h2">{c.ctaSectionTitle}</h2>
              <p className="sas-body mt-3 text-[15.5px] max-w-xl">
                {c.ctaSectionLead}
              </p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <LocalizedLink
                to="/contact/v2-feedback?source=v2_article"
                className="sas-btn sas-btn-primary w-full sm:w-auto sm:min-w-[220px] justify-center"
                data-testid="v2-cta-primary"
              >
                <MessageSquarePlus size={17} aria-hidden />
                <span>{c.ctaPrimary}</span>
              </LocalizedLink>
              <LocalizedLink
                to="/contact/volunteer?source=v2_article"
                className="sas-btn sas-btn-ghost w-full sm:w-auto sm:min-w-[220px] justify-center"
                data-testid="v2-cta-secondary"
              >
                <UserPlus size={17} aria-hidden />
                <span>{c.ctaSecondary}</span>
              </LocalizedLink>
            </div>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="sas-section-sm pt-4" data-testid="v2-closing">
        <div className="sas-container">
          <p className="text-[13px] text-[color:var(--sas-ink-2)] italic max-w-2xl">
            {c.closing}
          </p>
        </div>
      </section>
    </article>
  );
}
